import { Transition } from '@headlessui/react';
import { AnimatePresence } from 'framer-motion';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { FileIconVariant } from '../../assets/icons/file-icon';
import { FolderIconVariant } from '../../assets/icons/folder-icon';
import {
  ChevronDownIcon,
  CloseIcon,
  ExportIcon,
  FileIcon,
  FolderIcon,
  PlusIcon,
  SearchIcon,
} from '../../assets/icons/index';
import { AlertDialogWrapper } from '../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../components/button/index';
import { Input } from '../../components/input/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { CrossfadePanel } from '../../components/motion/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../../components/select/index';
import { Tooltip } from '../../components/tooltip/index';
import { QueryKey } from '../../constants/index';
import {
  useDebounce,
  useInvalidateQueries,
  useModalState,
  useSelectedCompany,
  VaultHooks,
} from '../../hooks/index';
import { StakeholderRole } from '../../types/stakeholder.types';
import {
  VaultFile,
  VaultFolder,
  VaultOrder,
  VaultSelect,
  VaultSelectVariant,
  VaultSortBy,
  VaultTab,
} from '../../types/vault.types';
import { getTranslation } from '../../utils/get-translation.util';
import Breadcrumbs from './breadcrumbs/breadcrumbs';
import { MobileBreadcrumbs } from './breadcrumbs/index';
import { EmptyVault } from './empty-vault';
import { VaultMobile } from './mobile/index';
import { AddFileModal, CreateModal } from './modals/index';
import { VaultTable } from './table/table';
import { VaultPlaceholder } from './vault-loading-placeholders';

const Vault: FC = () => {
  const [selectedTab, setTab] = useState<VaultTab>(VaultTab.REPOSITORY);
  const [selected, setSelected] = useState<VaultSelectVariant>();
  const [search, setSearch] = useState<null | string>(null);
  const [{ field: sortBy, sortMode: orderBy }, setSortState] = useState<{
    field?: VaultSortBy;
    sortMode?: VaultOrder;
  }>({});

  const [selectedFolders, setSelectedFolders] = useState<{ id: string; name: string }[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
  const isStakeholdersPath = selectedFolders.some((folder) => folder.name === 'Stakeholders');
  const isDocumentsPath = selectedFolders.some((folder) => folder.name === 'Documents');
  const restrictStakeholderFileActions = isStakeholdersPath && !isDocumentsPath;

  const { selectedCompany, selectedCompany: { roleInCompany } = {} } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';
  const isStakeholder = roleInCompany === StakeholderRole.STAKEHOLDER || false;

  const { create } = VaultHooks.useCreateFolder();
  const { deleteFolder } = VaultHooks.useDeleteFolder();
  const { deleteFile } = VaultHooks.useDeleteFile();
  const { renameFile } = VaultHooks.useRenameFile();
  const { renameFolder } = VaultHooks.useRenameFolder();
  const { mutateFolderExport } = VaultHooks.useFolderExport();
  const { downloadVaultFile } = VaultHooks.useDownloadVaultFile();
  const { mutateOpenDriveExport, isPending: isOpenDriveExportPending } =
    VaultHooks.useOpenDriveExport();

  const {
    files,
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
  } = VaultHooks.useRepositoryFiles({
    companyId,
    search: search || '',
    sortBy,
    orderBy,
    mode: selectedTab,
  });

  const { folders, isLoading, refetch } = VaultHooks.useRepositoryFolders({
    companyId,
    search: search || '',
    sortBy,
    orderBy,
    mode: selectedTab,
  });

  const {
    folder,
    isLoading: isFolderLoading,
    refetch: refetchVaultFolder,
  } = VaultHooks.useVaultFolder({
    companyId,
    folderId: selectedFolderId || '',
    sortBy,
    orderBy,
  });

  const data: {
    files: VaultFile[];
    folders: VaultFolder[];
    uploadPermission: boolean;
  } = !selectedFolderId
    ? {
        folders,
        files: selectedTab === VaultTab.REPOSITORY ? (search ? files : []) : files,
        uploadPermission: false,
      }
    : search
      ? { folders, files, uploadPermission: false }
      : {
          folders: folder?.folders || [],
          files: folder?.files || [],
          uploadPermission: folder?.uploadPermission || false,
        };

  const shouldShowAddFilesButton =
    data?.uploadPermission &&
    selectedTab === VaultTab.REPOSITORY &&
    roleInCompany !== StakeholderRole.STAKEHOLDER;

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_HARDCODED_REPOSITORY,
    QueryKey.GET_OPEN_REPOSITORY,
    QueryKey.GET_VAULT_FOLDER,
    QueryKey.GET_OPEN_FILES,
    QueryKey.GET_HARDCODED_REPOSITORY_FILES,
  );

  const isOpenDriveExportDisabled =
    selectedTab === VaultTab.OPEN_DRIVE &&
    !search &&
    !isLoading &&
    !isLoadingFiles &&
    folders.length === 0 &&
    files.length === 0;

  const isOpenDriveExportInactive = isOpenDriveExportDisabled || isOpenDriveExportPending;

  const handleOpenDriveExport = () => {
    mutateOpenDriveExport(undefined, {
      onError: () => toast.error(getTranslation('EXPORT_OPEN_DRIVE_FAILED')),
    });
  };

  const handleFolderClick = (folder: VaultFolder) => {
    setSelectedFolderId(folder.id);
    setSearch('');
    setSelectedFolders((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index: number, id: string | undefined) => {
    setSelectedFolders((prev) => prev.slice(0, index + 1));
    setSelectedFolderId(id);
    setSearch('');
  };

  const { debounceCallback } = useDebounce({
    callback: () => {
      invalidateQuery();
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      debounceCallback(value);
    },
    [debounceCallback],
  );

  const handleSortField = useCallback(
    (newField: VaultSortBy) => {
      setSortState((prevState) => {
        if (prevState.field === newField) {
          const nextSortMode = !prevState.sortMode
            ? VaultOrder.ASC
            : prevState.sortMode === VaultOrder.ASC
              ? VaultOrder.DESC
              : undefined;

          return { ...prevState, sortMode: nextSortMode };
        }
        return { field: newField, sortMode: VaultOrder.ASC };
      });
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const handleSelect = (select: VaultSelectVariant) => {
    if (select === VaultSelect.FOLDER) handleOpenCreateModal(VaultSelect.FOLDER);
    if (select === VaultSelect.FILE) handleOpenAddFileModal();
    setSelected(select);
  };

  const handleDeleteFolder = (id: string) => {
    deleteFolder(
      { companyId, folderId: id },
      {
        onError: ({ body }) => toast.error(body?.message),
        onSuccess: () => {
          toast.success(getTranslation('FOLDER_DELETED_SUCCESS'));
          invalidateQuery();
        },
      },
    );
  };

  const handleDeleteFile = (id: string) => {
    deleteFile(
      { companyId, fileId: id },
      {
        onError: ({ body }) => toast.error(body?.message),
        onSuccess: () => {
          toast.success(getTranslation('FILE_DELETED_SUCCESS'));
          invalidateQuery();
        },
      },
    );
  };

  const handleRenameFile = (id: string, name: string, link?: string) => {
    renameFile(
      {
        companyId,
        fileId: id,
        data: {
          name: name,
          folderId: selectedFolderId || undefined,
          link,
        },
      },
      {
        onError: ({ body }) => toast.error(body?.message),
        onSuccess: () => {
          toast.success(getTranslation('FILE_RENAMED_SUCCESS'));
          invalidateQuery();
        },
      },
    );
  };

  const handleRenameFolder = (id: string, name: string) => {
    renameFolder(
      {
        companyId,
        folderId: id,
        data: {
          name: name,
          parentFolderId: selectedFolderId || undefined,
        },
      },
      {
        onError: ({ body }) => toast.error(body?.message),
        onSuccess: () => {
          toast.success(getTranslation('FOLDER_RENAMED_SUCCESS'));
          invalidateQuery();
        },
      },
    );
  };

  const handleDownloadFolder = (id: string) => {
    mutateFolderExport({
      folderId: id,
    });
  };

  const handleDownloadDocument = (fileId: string) => {
    downloadVaultFile({ fileId });
  };

  const {
    toggler: createModalToggler,
    isOpen: isOpenCreateModal,
    handleOpenModal: handleOpenCreateModal,
    handleCloseModal: handleCloseCreateModal,
    handleSuccessModal: handleSuccessCreateModal,
  } = useModalState<string | undefined, unknown, VaultSelect>({
    onSuccess: (name?: string) => {
      create(
        {
          companyId,
          data: {
            name: name || '',
            parentFolderId: selectedFolderId || undefined,
          },
        },
        {
          onError: ({ body }) => toast.error(body?.message),
          onSuccess: () => {
            toast.success(<AppFormattedMessage id="FOLDER_CREATED_SUCCESSFULLY" />);
            invalidateQuery();
          },
        },
      );
    },
  });

  const {
    handleCloseModal: handleCloseAddFileModal,
    handleOpenModal: handleOpenAddFileModal,
    isOpen: isOpenAddFileModal,
  } = useModalState({});

  const folderNames = data?.folders?.map((folder: VaultFolder) => folder?.name?.toLowerCase());

  useEffect(() => {
    if (!search && companyId) {
      refetch();
      refetchVaultFolder();
      refetchFiles();
    }
  }, [companyId, refetch, refetchFiles, refetchVaultFolder, search]);

  return (
    <LoadingCrossfade
      isLoading={isLoading || isFolderLoading || isLoadingFiles}
      skeleton={<VaultPlaceholder />}
    >
      {() => (
        <>
          <AlertDialogWrapper
            control={{ onOpenChange: createModalToggler, open: isOpenCreateModal }}
          >
            <CreateModal
              folderNames={folderNames}
              onClose={handleCloseCreateModal}
              onSuccess={handleSuccessCreateModal}
            />
          </AlertDialogWrapper>

          <AddFileModal
            companyId={companyId}
            data={data}
            handleClose={handleCloseAddFileModal}
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenAddFileModal}
            isOpenRepository={selectedTab === VaultTab.OPEN_DRIVE}
            selectedFolderId={selectedFolderId}
          />

          <div className="flex h-fit min-h-full w-full flex-col overflow-x-auto rounded-md lg:shadow-sm">
            {!isStakeholder && (
              <div className="flex w-full gap-4 border-b-[1px] border-gray-200 xlg:px-4 lg:pt-6">
                <Button
                  className={twMerge(
                    'flex h-full w-fit rounded-none border-b-2 border-transparent pb-4 text-sm font-[450] text-gray-400',
                    selectedTab === VaultTab.REPOSITORY &&
                      'border-brand-700 font-[550] text-brand-700',
                  )}
                  onClick={() => {
                    setTab(VaultTab.REPOSITORY);
                    setSelectedFolderId(undefined);
                    setSelectedFolders([]);
                  }}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="REPOSITORY" />
                </Button>
                <Button
                  className={twMerge(
                    'flex h-full w-fit rounded-none border-b-2 border-transparent pb-4 text-sm font-[450] text-gray-400',
                    selectedTab === VaultTab.OPEN_DRIVE &&
                      'border-brand-700 font-[550] text-brand-700',
                  )}
                  onClick={() => {
                    setTab(VaultTab.OPEN_DRIVE);
                    setSelectedFolderId(undefined);
                    setSelectedFolders([]);
                  }}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="OPEN_DRIVE" />
                </Button>
              </div>
            )}
            <div className="hidden w-full items-center justify-between border-b-[1px] border-gray-200 xlg:flex xlg:p-4">
              <Breadcrumbs
                onBreadcrumbClick={handleBreadcrumbClick}
                selectedFolders={selectedFolders}
              />
              <div className="flex max-h-9 w-fit items-center gap-3">
                <Input
                  icon={search ? <CloseIcon /> : <SearchIcon />}
                  inputClassName="h-9"
                  onChange={handleSearch}
                  onIconClick={() => {
                    setTimeout(() => {
                      setSearch('');
                      setSelectedFolders([]);
                      setSelectedFolderId('');
                      invalidateQuery();
                    }, 0);
                  }}
                  placeholder={search ? '' : <AppFormattedMessage id="SEARCH" />}
                  value={search || ''}
                  wrapperClassName={'transition-all duration-1000 min-w-[303px] h-9'}
                />
                {shouldShowAddFilesButton && (
                  <Button
                    className="w-fit min-w-[113px] gap-1 text-nowrap p-2"
                    data-permission-required
                    onClick={handleOpenAddFileModal}
                    styleType="BLUE_ROUNDED"
                  >
                    <PlusIcon />
                    <AppFormattedMessage id="ADD_FILES" />
                  </Button>
                )}
                {selectedTab === VaultTab.OPEN_DRIVE && !isStakeholder && (
                  <Tooltip
                    content={<AppFormattedMessage id="OPEN_DRIVE_EXPORT_EMPTY_TOOLTIP" />}
                    disabled={!isOpenDriveExportDisabled}
                  >
                    <Button
                      className={twMerge(
                        'inline-flex h-9 w-[170px] items-center justify-center gap-1 border border-gray-100 bg-white px-3 py-[6px] shadow-xs',
                        'disabled:cursor-not-allowed disabled:bg-gray-50',
                      )}
                      disabled={isOpenDriveExportInactive}
                      disabledStyle={ButtonDisableStyle.NONE}
                      onClick={handleOpenDriveExport}
                      styleType="DEFAULT_ROUNDED"
                    >
                      <ExportIcon
                        className="rotate-180"
                        iconColor={isOpenDriveExportInactive ? '#D0D5DD' : '#344054'}
                      />
                      <span
                        className={twMerge(
                          'text-sm font-[450]',
                          isOpenDriveExportInactive ? 'text-gray-300' : 'text-gray-700',
                        )}
                      >
                        <AppFormattedMessage id="EXPORT_OPEN_DRIVE" />
                      </span>
                    </Button>
                  </Tooltip>
                )}
                {selectedTab === VaultTab.OPEN_DRIVE && (
                  <Select onChange={handleSelect} value={selected}>
                    {({ open }) => (
                      <>
                        <div
                          className="relative rounded border-gray-200 px-[6px]"
                          data-permission-required
                        >
                          <SelectButton className="bg-brand-700">
                            <PlusIcon />
                            <span className="text-sm font-[450] text-white">
                              <AppFormattedMessage id="ADD" />
                            </span>
                            <ChevronDownIcon className="ml-2 h-4 w-4" iconColor="#ffffff" />
                          </SelectButton>

                          <Transition
                            as="div"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            show={open}
                          >
                            <SelectOptions className="w-[115px]">
                              {selectedFolders.length < 7 && (
                                <SelectOption
                                  className="flex items-center gap-[6px]"
                                  key={VaultSelect.FOLDER}
                                  selectedClassName="bg-white"
                                  value={VaultSelect.FOLDER}
                                >
                                  <FolderIcon
                                    fill="#EAECF0"
                                    iconColor="#344054"
                                    variant={FolderIconVariant.FILLED}
                                  />
                                  <span className="block text-sm font-normal text-gray-700">
                                    <AppFormattedMessage id="FOLDER" />
                                  </span>
                                </SelectOption>
                              )}
                              <SelectOption
                                className="flex items-center gap-[6px]"
                                key={VaultSelect.FILE}
                                selectedClassName="bg-white"
                                value={VaultSelect.FILE}
                              >
                                <FileIcon variant={FileIconVariant.FILLED} />
                                <span className="block text-sm font-normal text-gray-700">
                                  <AppFormattedMessage id="FILE" />
                                </span>
                              </SelectOption>
                            </SelectOptions>
                          </Transition>
                        </div>
                      </>
                    )}
                  </Select>
                )}
              </div>
            </div>
            <div className="flex flex-col pt-4 xlg:hidden">
              <Input
                icon={search ? <CloseIcon /> : <SearchIcon />}
                inputClassName="border-none"
                onChange={handleSearch}
                onIconClick={() => {
                  setTimeout(() => {
                    setSearch('');
                    invalidateQuery();
                  }, 0);
                }}
                placeholder={<AppFormattedMessage id="SEARCH" />}
                value={search || ''}
                wrapperClassName={'transition-all duration-1000 w-full'}
              />
              <MobileBreadcrumbs
                onBreadcrumbClick={handleBreadcrumbClick}
                selectedFolders={selectedFolders}
              />
            </div>
            <AnimatePresence mode="wait">
              <CrossfadePanel
                className="flex w-full flex-col"
                key={selectedTab}
                panelKey={selectedTab}
                translate={false}
              >
                <VaultTable
                  className="hidden xlg:block"
                  data={data}
                  handleDeleteFile={handleDeleteFile}
                  handleDeleteFolder={handleDeleteFolder}
                  handleDownloadDocument={handleDownloadDocument}
                  handleDownloadFolder={handleDownloadFolder}
                  handleFolderClick={handleFolderClick}
                  handleRenameFile={handleRenameFile}
                  handleRenameFolder={handleRenameFolder}
                  isStakeholdersPath={restrictStakeholderFileActions}
                  onSortClick={handleSortField}
                  searchValue={search || ''}
                  selectedTab={selectedTab}
                  sortField={sortBy}
                  sortMode={orderBy}
                />
                <VaultMobile
                  className="block xlg:hidden"
                  data={data}
                  handleDeleteFile={handleDeleteFile}
                  handleDeleteFolder={handleDeleteFolder}
                  handleDownloadDocument={handleDownloadDocument}
                  handleDownloadFolder={handleDownloadFolder}
                  handleFolderClick={handleFolderClick}
                  handleRenameFile={handleRenameFile}
                  handleRenameFolder={handleRenameFolder}
                  isStakeholdersPath={restrictStakeholderFileActions}
                  onBackClick={handleBreadcrumbClick}
                  selectedFolders={selectedFolders}
                  selectedTab={selectedTab}
                />
                {data?.folders?.length === 0 &&
                  data?.files?.length === 0 &&
                  selectedTab == VaultTab.REPOSITORY && (
                    <EmptyVault
                      onAddFile={() => ''}
                      onAddFolder={() => ''}
                      selectedTab={selectedTab}
                    />
                  )}
                {data?.folders?.length === 0 &&
                  data?.files?.length === 0 &&
                  selectedTab == VaultTab.OPEN_DRIVE && (
                    <EmptyVault
                      fileButtonDisabled={false}
                      folderButtonDisabled={selectedFolders.length >= 7}
                      onAddFile={handleOpenAddFileModal}
                      onAddFolder={handleOpenCreateModal}
                      selectedTab={selectedTab}
                    />
                  )}
              </CrossfadePanel>
            </AnimatePresence>
          </div>
        </>
      )}
    </LoadingCrossfade>
  );
};

export default Vault;
